import React, {useMemo} from "react";
import { Alert, Divider, Stack, Typography, TypographyProps, Box } from "@mui/material";
import {CheckoutFormTypeFields, CheckoutProcessStep, useCheckoutForm} from "./hooks/useCheckoutForm";
import { EnvelopeSimpleIcon, ReceiptIcon, StorefrontIcon, CreditCardIcon, } from "@phosphor-icons/react";
import {OrderDeliveryAddressFields, OrderPersonalInformationFields} from "../../types/purchaseOrder/Order";

import {
    OrderPaymentOptionViewDTO,
    OrderPaymentOptionViewDTOFields,
} from "../../types/purchaseOrder/Order";
import {STYLES} from "../ShoppingCart/ShoppingCartStyles";

const MP_LOGO_URL = "images/Mercado_Pago.svg";


type PaymentMeta = {
    label: string;
    description?: string | null;
    provider?: string | null;
    icon?: React.ReactNode;
    isMercadoPago: boolean;
};

const resolvePaymentMeta = (
    paymentOptionId: string | undefined,
    paymentOptions: OrderPaymentOptionViewDTO[]
): PaymentMeta | null => {
    if (!paymentOptionId) return null;

    const selected = (paymentOptions ?? []).find((o: any) => String(o?.id ?? "") === String(paymentOptionId));
    if (!selected) return null;

    const providerCode = selected?.[OrderPaymentOptionViewDTOFields.ProviderCode];
    const provider = String(selected?.[OrderPaymentOptionViewDTOFields.ProviderName] ?? "").trim() || null;
    const label = String(selected?.[OrderPaymentOptionViewDTOFields.OptionName] ?? "").trim() || "Pago";
    const description = String(selected?.[OrderPaymentOptionViewDTOFields.Description] ?? "").trim() || null;

    const isMercadoPago = providerCode === 1;

    return {
        label,
        provider,
        description,
        isMercadoPago,
        icon: isMercadoPago ? (
            <Box
                component="img"
                src={MP_LOGO_URL}
                alt="Mercado Pago"
                loading="lazy"
                sx={{
                    height: 28,
                    width: "auto",
                    display: "block",
                }}
            />
        ) : undefined,
    };
};


export const PaymentSection: React.FC = () => {
  const { 
    methods, setCurrentStep, zipCode,
      deliveryType, deliveryOptions, isHomeDelivery, paymentOptions,
      paymentOptionId,
  } = useCheckoutForm();
  const watchPersonalInformation = methods.watch(CheckoutFormTypeFields.PersonalInformation);
  const watchDeliveryAddress = methods.watch(CheckoutFormTypeFields.DeliveryAddress);
  const watchEmail = methods.watch(`${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Email}`);
  const deliveryOption = useMemo(() => {
    if (!deliveryType) return null;
    
    return deliveryOptions
      .flatMap((g: any) => g.options)
      .find((o: any) => `${o.value}` === `${deliveryType}`);
  }, [deliveryType, deliveryOptions])  

    const paymentMeta = useMemo(
        () => resolvePaymentMeta(paymentOptionId, paymentOptions),
        [paymentOptionId, paymentOptions]
    );
  
  const changeButtonComponent = (
    <Typography fontFamily={'Rubik'} 
                fontSize={'14px'}
                fontWeight={600}
                onClick={() => setCurrentStep(CheckoutProcessStep.CustomerDetails)}
                sx={{ '&:hover': { opacity: .6, cursor: 'pointer' } }}
    >
      Cambiar
    </Typography>
  )

    const paymentSummaryNode = (
        <Stack spacing={0.6} sx={{ minWidth: 0 }}>
            <TitleLabel>Medio de Pago</TitleLabel>

            {!paymentMeta ? (
                <SubtitleLabel>-</SubtitleLabel>
            ) : paymentMeta.isMercadoPago ? (
                <Box sx={{ pt: 0.25 }}>
                    <Box
                        component="img"
                        src={MP_LOGO_URL}
                        alt="Mercado Pago"
                        loading="lazy"
                        sx={{
                            height: 46,
                            width: "auto",
                            display: "block",
                        }}
                    />
                </Box>
            ) : (
                <SubtitleLabel>{paymentMeta.label}</SubtitleLabel>
            )}
        </Stack>
    );


  
  return (
    <Stack mb={2} spacing={2}>
      <Alert severity={'info'}
             sx={{
               fontFamily: 'Rubik',
               fontSize: '14px',
               backgroundColor: 'rgba(127, 140, 141, .15)',
               border: '1px solid #bce8f1',
               borderRadius: '10px',
               borderColor: 'rgba(127, 140, 141, .2)',
               color: '#6d6d6d',
               '& > div > svg': {
                 color: '#6d6d6d',
               }
             }}
      >
        Estás a UN PASO de terminar tu orden! Recordá deslizar hacia abajo y dar click en el botón REALIZAR PEDIDO
      </Alert>
      
      <Stack spacing={2}
             paddingX={3.85}
             paddingY={2}
             sx={{ border: '1px solid rgba(1,1,1,.1)', }}
      >
        <PaymentSectionComponent Icon={EnvelopeSimpleIcon}>
            <Typography
                fontFamily="Rubik"
                fontSize="14px"
                sx={{
                opacity: 0.8,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                }}
            >
                {watchEmail}
            </Typography>

        </PaymentSectionComponent>
        
        <Divider />

        <PaymentSectionComponent Icon={StorefrontIcon}
                                 action={changeButtonComponent}
        >
          <Stack spacing={0.6}>
            {
              deliveryOption ?
                <Stack direction={'row'} justifyContent={'space-between'} width={1}>
                  <Stack>
                    <TitleLabel children={deliveryOption.summary} />
                    
                    <SubtitleLabel children={deliveryOption.description} />
                  </Stack>
                </Stack>
                :
                "-"
            }
          </Stack>
        </PaymentSectionComponent>

        <Divider />

        <PaymentSectionComponent Icon={ReceiptIcon}
                                 action={changeButtonComponent}
        >
          <Stack spacing={0.6}>
            <TitleLabel children={"Datos de cobranza"} />
            
            <SubtitleLabel children={`${watchPersonalInformation[OrderPersonalInformationFields.FirstName]} ${watchPersonalInformation[OrderPersonalInformationFields.LastName]}`} />

            {
              isHomeDelivery &&
                <SubtitleLabel children={`${watchDeliveryAddress[OrderDeliveryAddressFields.Street]} ${watchDeliveryAddress[OrderDeliveryAddressFields.StreetNumber] || ''}`} />
            }
            
            { !!zipCode && <SubtitleLabel children={`CP ${zipCode || ''}`} /> }

            <SubtitleLabel children={`${watchPersonalInformation[OrderPersonalInformationFields.AreaCode]}${watchPersonalInformation[OrderPersonalInformationFields.PhoneNumber]}`} />
          </Stack>
        </PaymentSectionComponent>

        <Divider />

        <PaymentSectionComponent Icon={CreditCardIcon} action={changeButtonComponent}>
            {paymentSummaryNode}
        </PaymentSectionComponent>


      </Stack>
      
         
      
    </Stack>
  )
};

interface PaymentSectionComponentProps {
  Icon: React.ElementType,
  children: React.ReactElement,
  action?: React.ReactElement
}

const PaymentSectionComponent: React.FC<PaymentSectionComponentProps> = ({ Icon, children, action }) => {
    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ gap: 2, minWidth: 0 }}
        >
            <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{ flex: 1, minWidth: 0 }}
            >
                <Icon size={23} color={STYLES.colors.secondary} />

                {/* minWidth:0 + flex para que el texto pueda wrappear/ellipsis */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Stack>

            {!!action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
    );
};


const TitleLabel: React.FC<TypographyProps> = (props) => (
    <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{
            color: "#1b1b1b",
            fontSize: "14px",
            fontFamily: "Rubik",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
        }}
        {...props}
    />
);


const SubtitleLabel: React.FC<TypographyProps> = (props) => (
    <Typography
        variant="body2"
        color="text.secondary"
        sx={{
            color: "#1b1b1b",
            opacity: 0.8,
            fontSize: "13px",
            fontFamily: "Rubik",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
        }}
        {...props}
    />
);














